import { useState, useCallback } from 'react';
import {
  createPDFViewerData,
  createSnippetViewerData,
  createChunkViewerData,
} from '../utils/viewerHelpers';
import { parseStudioMarkdown } from '../utils/studioMarkdown';
import { normalizeStudioResult, extractContentText } from '../utils/studioUtils';
import type { ViewerData } from '../types/viewer';
import type { SavedContent } from '../services/savedContentService';
import type { MainView } from './useNavigationStack';

export type OpenViewerOptions = {
  filename?: string;
  documentPath?: string;
  displayName?: string;
  documentId?: string;
  page?: number;
  highlightText?: string;
  chunkId?: string;
  chunkText?: string;
  snippetCoordinates?: { x: number; y: number; width: number; height: number };
  snippetText?: string;
  snippetTitle?: string;
  snippetImageData?: string;
  scope?: 'private' | 'shared';
  view_url?: string; // URL provided by backend (preferred over constructing URLs)
  metadata?: Record<string, unknown>;
};

export type ViewerCitationData = {
  document_name: string;
  document_display_name?: string;
  page: number;
  text: string;
  document_type?: string;
  company_name?: string;
  document_period?: string;
  document_id?: string;
  chunk_id?: string;
  documentPath?: string;
};

export interface UseViewerManagementOptions {
  // navigateToView accepts MainView to match the actual function signature from AppShell
  // Even though this hook only calls it with 'viewer', the type should match the actual function
  navigateToView: (
    view: MainView,
    data?: ViewerData | Record<string, unknown>,
    label?: string
  ) => void;
  isDriveFullscreen: boolean;
  setIsDriveFullscreen: (value: boolean) => void;
  setIsDriveOpen: (value: boolean) => void;
}

export interface UseViewerManagementReturn {
  activeViewer: ViewerData | null;
  setActiveViewer: React.Dispatch<React.SetStateAction<ViewerData | null>>;
  resolveViewerToken: () => string | null;
  openInViewer: (options: OpenViewerOptions) => void;
  openCitationInViewer: (citationData: ViewerCitationData) => void;
  openChunkInViewer: (citationData: ViewerCitationData) => void;
  handleStudioResultView: (studioResult: SavedContent) => void;
}

/**
 * Helper function to close other panels when opening viewer
 */
const closeOtherPanels = (
  isDriveFullscreen: boolean,
  setIsDriveFullscreen: (value: boolean) => void,
  setIsDriveOpen: (value: boolean) => void
) => {
  if (isDriveFullscreen) {
    setIsDriveFullscreen(false);
  }
  setIsDriveOpen(false);
};

/**
 * Helper function to create snippet payload
 */
const createSnippetPayload = (
  options: OpenViewerOptions,
  mergedMetadata: Record<string, unknown>
) => ({
  id: (options.metadata?.snippetId as string) || `snippet-${Date.now()}`,
  document_id: options.documentId,
  document_name: options.displayName || options.filename,
  page_number: options.page ?? 1,
  title: options.snippetTitle,
  text_content: options.snippetText,
  coordinates: options.snippetCoordinates,
  image_data: options.snippetImageData,
  metadata: mergedMetadata,
});

/**
 * Helper function to create chunk payload
 */
const createChunkPayload = (
  options: OpenViewerOptions,
  mergedMetadata: Record<string, unknown>
) => ({
  chunk_id: options.chunkId || `chunk-${Date.now()}`,
  document_id: options.documentId,
  document_name: options.displayName || options.filename,
  text: options.chunkText || options.highlightText || '',
  page_number: options.page ?? 1,
  metadata: mergedMetadata,
});

/**
 * Hook para gestionar el viewer de documentos
 *
 * Maneja:
 * - Estado del viewer activo
 * - Resolución de tokens para autenticación
 * - Apertura de diferentes tipos de contenido (PDF, snippets, chunks)
 * - Navegación al viewer
 */
export function useViewerManagement({
  navigateToView,
  isDriveFullscreen,
  setIsDriveFullscreen,
  setIsDriveOpen,
}: UseViewerManagementOptions): UseViewerManagementReturn {
  const [activeViewer, setActiveViewer] = useState<ViewerData | null>(null);

  const resolveViewerToken = useCallback(() => {
    return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  }, []);

  const openInViewer = useCallback(
    (options: OpenViewerOptions) => {
      // Prepare metadata
      const resolvedDocumentId = options.documentId ?? options.metadata?.document_id ?? undefined;
      const mergedMetadata = {
        ...(options.metadata || {}),
      };

      if (resolvedDocumentId && !mergedMetadata.document_id) {
        mergedMetadata.document_id = resolvedDocumentId;
      }

      if (options.scope) {
        mergedMetadata.scope = options.scope;
      }

      // Handle Studio result viewer (debe ir ANTES de snippet para que se detecte primero)
      if (options.snippetText && mergedMetadata.type === 'studio-result') {
        const studioData: ViewerData = {
          type: 'studio-result',
          title: options.snippetTitle || options.displayName || 'Studio',
          textContent: options.snippetText,
          metadata: mergedMetadata,
        };
        setActiveViewer(studioData);
        closeOtherPanels(isDriveFullscreen, setIsDriveFullscreen, setIsDriveOpen);
        navigateToView(
          'viewer',
          studioData,
          `Studio: ${options.snippetTitle || options.displayName || 'Studio'}`
        );
        return;
      }

      // Handle snippet viewer
      if (options.snippetText) {
        const snippetPayload = createSnippetPayload(options, mergedMetadata);
        const snippetData = createSnippetViewerData(options.snippetText, snippetPayload);
        setActiveViewer(snippetData);
        closeOtherPanels(isDriveFullscreen, setIsDriveFullscreen, setIsDriveOpen);
        navigateToView(
          'viewer',
          snippetData,
          `Snippet: ${options.snippetTitle || snippetPayload.document_name || 'Snippet'}`
        );
        return;
      }

      // Handle chunk viewer
      if (options.chunkId || options.chunkText) {
        const chunkPayload = createChunkPayload(options, mergedMetadata);
        const chunkData = createChunkViewerData(chunkPayload);
        setActiveViewer(chunkData);
        closeOtherPanels(isDriveFullscreen, setIsDriveFullscreen, setIsDriveOpen);
        navigateToView('viewer', chunkData, `Chunk: ${chunkPayload.document_name || 'Chunk'}`);
        return;
      }

      // Handle PDF viewer
      // Use view_url from options if available (provided by backend), otherwise fallback to documentPath
      const pdfDisplayName =
        options.displayName || options.filename || options.documentPath || 'Detalle';

      const citation = {
        view_url: options.metadata?.view_url || options.view_url, // Use view_url from backend if available
        documentPath: options.documentPath, // Fallback to documentPath if view_url not available
        documentName: options.filename || pdfDisplayName,
        documentDisplayName: pdfDisplayName,
        document_id: resolvedDocumentId,
        page: options.page ?? 1,
        highlightText: options.highlightText ?? '',
        chunk_id: options.chunkId,
        snippet_coordinates: options.snippetCoordinates,
        metadata: mergedMetadata,
      };

      const viewerData = createPDFViewerData(citation);
      setActiveViewer(viewerData);
      closeOtherPanels(isDriveFullscreen, setIsDriveFullscreen, setIsDriveOpen);
      navigateToView('viewer', viewerData, `PDF: ${pdfDisplayName}`);
    },
    [isDriveFullscreen, setIsDriveFullscreen, setIsDriveOpen, navigateToView]
  );

  const openCitationInViewer = useCallback(
    (citationData: ViewerCitationData) => {
      if (!citationData?.document_name) {
        return;
      }

      openInViewer({
        filename: citationData.document_name,
        displayName: citationData.document_display_name || citationData.document_name,
        documentId: citationData.document_id,
        page: citationData.page,
        highlightText: citationData.text,
        chunkId: citationData.chunk_id,
      });
    },
    [openInViewer]
  );

  const openChunkInViewer = useCallback(
    (citationData: ViewerCitationData) => {
      if (!citationData?.chunk_id && !citationData?.text) {
        return;
      }

      openInViewer({
        documentId: citationData.document_id,
        displayName: citationData.document_display_name || citationData.document_name,
        documentPath: citationData.documentPath,
        page: citationData.page,
        chunkId: citationData.chunk_id,
        chunkText: citationData.text,
        metadata: {
          document_type: citationData.document_type,
          company_name: citationData.company_name,
          document_period: citationData.document_period,
        },
      });
    },
    [openInViewer]
  );

  const handleStudioResultView = useCallback(
    (studioResult: SavedContent) => {
      // El contenido guardado puede tener formato:
      // 1. String markdown directo
      // 2. Objeto con { markdown: "...", raw: {...} }
      const markdownString =
        typeof studioResult.content === 'string'
          ? studioResult.content
          : studioResult.content?.markdown ?? '';

      // También extraer rawResult si existe (contiene el result_data completo)
      const rawResultFromContent =
        typeof studioResult.content === 'object' && studioResult.content?.raw
          ? studioResult.content.raw
          : null;

      const parsed = parseStudioMarkdown(markdownString);
      const metadata = parsed.data as Record<string, any>;
      const title =
        metadata.title || studioResult.title || studioResult.studio_name || 'Resultado Studio';

      // Procesar el contenido usando el mismo flujo que StudiosPanel
      // Esto asegura que el renderizado sea idéntico en ambos lugares

      let processedContent = parsed.content; // Fallback al contenido original
      let normalized: ReturnType<typeof normalizeStudioResult> = null;

      // Construir rawResult similar al que viene del backend para que normalizeStudioResult funcione igual
      // El contenido guardado puede tener:
      // 1. metadata.result_data con estructura JSON (mejor caso)
      // 2. Contenido markdown con "**Pregunta:** {json} **Respuesta:** ..." (donde el JSON en Pregunta ES el result_data)

      // Intentar extraer result_data de múltiples lugares (prioridad):
      // 1. metadata.result_data (del front-matter)
      // 2. metadata.rawResult (del front-matter)
      // 3. rawResultFromContent.assistant_message.attachments.studio_metadata.result_data (del contenido guardado)
      let resultDataToProcess = metadata.result_data || metadata.rawResult || null;

      // Si no hay en metadata, intentar desde rawResultFromContent
      if (!resultDataToProcess && rawResultFromContent) {
        if (rawResultFromContent.assistant_message?.attachments?.studio_metadata?.result_data) {
          resultDataToProcess =
            rawResultFromContent.assistant_message.attachments.studio_metadata.result_data;
        } else if (rawResultFromContent.attachments?.studio_metadata?.result_data) {
          resultDataToProcess = rawResultFromContent.attachments.studio_metadata.result_data;
        } else if (rawResultFromContent.result_data) {
          resultDataToProcess = rawResultFromContent.result_data;
        }
      }

      // Si no hay result_data en metadata, intentar extraerlo del contenido markdown
      // El contenido guardado tiene formato: "**Pregunta:** {json} **Respuesta:** ..."
      // El JSON en "Pregunta" ES el result_data completo
      if (!resultDataToProcess && parsed.content) {
        try {
          const trimmed = parsed.content.trim();

          // Extraer la sección "Pregunta" (puede contener JSON)
          const preguntaMatch = trimmed.match(
            /\*\*Pregunta:\*\*\s*([\s\S]*?)(?=\*\*Respuesta:\*\*|$)/
          );

          if (preguntaMatch) {
            const preguntaContent = preguntaMatch[1].trim();
            let preguntaJson: any = null;

            // Intentar parsear la pregunta como JSON (múltiples métodos)
            // Método 1: JSON en bloque markdown ```json ... ```
            const jsonBlockMatch = preguntaContent.match(/```(?:json)?\s*\n([\s\S]*?)\n```/);
            if (jsonBlockMatch) {
              try {
                preguntaJson = JSON.parse(jsonBlockMatch[1].trim());
              } catch {
                // Continuar
              }
            }

            // Método 2: JSON directo (buscar { } balanceados)
            if (!preguntaJson) {
              const firstBrace = preguntaContent.indexOf('{');
              if (firstBrace !== -1) {
                let braceCount = 0;
                let jsonEnd = -1;

                for (let i = firstBrace; i < preguntaContent.length; i++) {
                  if (preguntaContent[i] === '{') braceCount++;
                  if (preguntaContent[i] === '}') {
                    braceCount--;
                    if (braceCount === 0) {
                      jsonEnd = i;
                      break;
                    }
                  }
                }

                if (jsonEnd !== -1) {
                  try {
                    preguntaJson = JSON.parse(preguntaContent.substring(firstBrace, jsonEnd + 1));
                  } catch {
                    // Continuar
                  }
                }
              }
            }

            // Método 3: Pregunta completa es JSON
            if (!preguntaJson && preguntaContent.startsWith('{') && preguntaContent.endsWith('}')) {
              try {
                preguntaJson = JSON.parse(preguntaContent);
              } catch {
                // Continuar
              }
            }

            // Si se encontró JSON en la pregunta, ESE ES el result_data completo
            if (preguntaJson) {
              resultDataToProcess = preguntaJson;
            }
          }
        } catch {
          // Error extrayendo JSON de pregunta
        }
      }

      // Si tenemos result_data (de metadata o extraído de Pregunta), procesarlo igual que StudiosPanel
      if (resultDataToProcess) {
        const rawResult = {
          assistant_message: {
            content: parsed.content,
            attachments: {
              studio_metadata: {
                result_data: resultDataToProcess,
              },
            },
            citations: metadata.citations || {},
            usage_stats: metadata.usage_stats,
            rag_stats: metadata.rag_stats,
            created_at: studioResult.created_at || new Date().toISOString(),
          },
          metadata: {
            ...metadata,
            usage_stats: metadata.usage_stats,
            rag_stats: metadata.rag_stats,
          },
          usage_stats: metadata.usage_stats,
          rag_stats: metadata.rag_stats,
          cached: metadata.cached || false,
          generated_at:
            metadata.generated_at || studioResult.created_at || new Date().toISOString(),
        };

        // Normalizar el resultado igual que StudiosPanel
        normalized = normalizeStudioResult(rawResult);

        if (normalized && normalized.result_data) {
          // Aplicar extractContentText para convertir estructura a markdown formateado
          // Esto es lo mismo que hace StudiosPanel
          const extracted = extractContentText(normalized.result_data);

          if (extracted && extracted.trim().length > 0) {
            processedContent = extracted;
          }
        }
      }

      // IMPORTANTE: Pasar el markdownString completo (con front-matter) para que DocumentViewer
      // pueda parsearlo correctamente. El procesamiento se hará dentro de DocumentViewer
      // usando el mismo flujo que StudiosPanel.
      // También pasar el rawResult si existe para que DocumentViewer pueda usarlo directamente
      openInViewer({
        displayName: title,
        snippetTitle: title,
        snippetText: markdownString, // Pasar el markdown completo, no el procesado
        metadata: {
          type: 'studio-result',
          title,
          markdown: markdownString,
          front_matter: metadata,
          normalized_result: normalized,
          // Pasar rawResult si existe para que DocumentViewer pueda usarlo directamente
          raw:
            rawResultFromContent ||
            (typeof studioResult.content === 'object' ? studioResult.content : null),
          studio_id: studioResult.studio_id,
          studio_name: studioResult.studio_name,
          studio_type: studioResult.studio_type,
          tags: studioResult.tags,
          document_ids: studioResult.document_ids,
          document_names: studioResult.document_names,
          created_at: studioResult.created_at,
          updated_at: studioResult.updated_at,
        },
      });
    },
    [openInViewer]
  );

  return {
    activeViewer,
    setActiveViewer,
    resolveViewerToken,
    openInViewer,
    openCitationInViewer,
    openChunkInViewer,
    handleStudioResultView,
  };
}
